import { useCallback, useRef, useState } from "react";

const useCamera = () => {
  // Refs essenciais
  const previewRef = useRef<HTMLVideoElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Estados mínimos para UI
  const [recording, setRecording] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [isCaptured, setIsCaptured] = useState<boolean>(false);
  const [videoSize] = useState<string>(""); // Para compatibilidade

  // Variáveis diretas como no HTML
  const mimeType = "video/mp4;codecs=vp9,mp4a.40.2";
  let mediaRecorder: MediaRecorder | null = null;
  let recordedChunks: Blob[] = [];

  /**
   * INICIAR GRAVAÇÃO - IGUAL AO HTML (mas sem download automático)
   */
  const handleStart = useCallback(async () => {
    try {
      setPermissionError(null);

      if (!MediaRecorder.isTypeSupported(mimeType)) {
        setPermissionError("Este formato não é suportado pelo seu navegador.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      
      if (previewRef.current) {
        previewRef.current.srcObject = stream;
      }

      recordedChunks = [];
      chunksRef.current = []; // Sincronizar com ref
      mediaRecorder = new MediaRecorder(stream, { mimeType });

      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          recordedChunks.push(event.data);
          chunksRef.current.push(event.data); // Sincronizar com ref
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setVideoUrl(url); // Salvar para uso posterior
      };

      mediaRecorder.start();
      setRecording(true);

    } catch (error) {
      console.error("Erro ao iniciar gravação:", error);
      setPermissionError("Erro ao acessar câmera ou microfone. Verifique as permissões do navegador.");
    }
  }, []);

  /**
   * PARAR GRAVAÇÃO - IGUAL AO HTML
   */
  const handleStop = useCallback(() => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setRecording(false);

      // Para a câmera igual ao HTML
      if (previewRef.current?.srcObject) {
        (previewRef.current.srcObject as MediaStream).getTracks().forEach(track => track.stop());
      }
    }
  }, []);

  /**
   * Captura frame do vídeo (necessário para onBoarding)
   */
  const handleCapture = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext("2d");
      const aspectRatio = 4 / 3;
      const videoWidth = videoRef.current.videoWidth;
      const videoHeight = videoRef.current.videoHeight;

      let width = videoWidth;
      let height = width / aspectRatio;

      if (height > videoHeight) {
        height = videoHeight;
        width = height * aspectRatio;
      }

      canvasRef.current.width = width;
      canvasRef.current.height = height;

      context?.drawImage(videoRef.current, 0, 0, width, height);
      setIsCaptured(true);

      if (videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
      }
    }
  }, []);

  /**
   * Converte vídeo para base64 (necessário para onBoarding)
   */
  const createVideoBase64 = useCallback(async (
    url: string
  ): Promise<{
    base64Content: string;
    fileExtension: string;
    mimeType: string;
  }> => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const mimeType = blob.type;
      const fileExtension = mimeType.split("/")[1]?.split(";")[0] || "mp4";
      
      const reader = new FileReader();
      const base64Content = await new Promise<string>((resolve, reject) => {
        reader.readAsDataURL(blob);
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
      });

      return { base64Content, fileExtension, mimeType };
    } catch (error) {
      console.error("Erro ao processar vídeo:", error);
      throw error;
    }
  }, []);

  /**
   * Download manual (para uso HTML simples)
   */
  const downloadVideo = useCallback(() => {
    if (videoUrl) {
      const a = document.createElement("a");
      a.href = videoUrl;
      a.download = "gravacao.mp4";
      a.click();
      URL.revokeObjectURL(videoUrl);
    }
  }, [videoUrl]);

  return {
    // Refs
    previewRef,
    videoRef,
    canvasRef,
    chunksRef,
    
    // Estados
    recording,
    permissionError,
    videoUrl,
    videoSize,
    isCaptured,
    mediaRecorder, // Retorna para compatibilidade

    // Funções
    handleStart,
    handleStop,
    handleCapture,
    createVideoBase64,
    downloadVideo,
    setVideoUrl,
    setIsCaptured,
  };
};

export default useCamera;
